import {forwardRef, useImperativeHandle} from "react"
import {Elements, PaymentElement, useElements} from "@stripe/react-stripe-js"
import {loadStripe, type Stripe, type StripeElementLocale} from "@stripe/stripe-js"
import {useTranslation, Trans} from "react-i18next"
import {queryOptions, useSuspenseQuery} from "@tanstack/react-query"
import {TFunction} from "i18next"

export const stripeQuery = (publishableKey: string, t: TFunction) => {
	return queryOptions({
		queryKey: ["stripe", publishableKey],
		queryFn: () =>
			loadStripe(publishableKey)
				.then((stripe) => {
					if (!stripe) {
						throw new Error("Stripe failed to load")
					}
					return stripe
				})
				.catch((error) => {
					console.error("Failed to load Stripe", error)
					throw {
						Message: <Trans i18nKey="payment_arrangement_payment_form_page.stripe.error.message" />,
						title: t("payment_arrangement_payment_form_page.stripe.error.title"),
						error: error,
					}
				}),
		staleTime: 1000 * 60 * 5,
		retry: 3,
		refetchOnWindowFocus: false,
	})
}

export const confirmSetup = async (stripe: Stripe, clientSecret: string, confirmationTokenID: string, t: TFunction) => {
	const confirmResult = await stripe.confirmSetup({
		redirect: "if_required",
		clientSecret: clientSecret,
		confirmParams: {
			confirmation_token: confirmationTokenID,
		},
	})

	if (confirmResult.error) throw confirmResult.error
	if (confirmResult.setupIntent?.status !== "succeeded") {
		throw {Message: t("unexpected_error.message")}
	}

	return {
		setupIntent: confirmResult.setupIntent,
	}
}

export const PaymentMethodForm = forwardRef(
	(
		{
			CustomerCountryISOAlpha,
			clientSecret,
			stripeLocale,
			PublishableKey,
			showStripeTerms,
		}: {
			CustomerCountryISOAlpha: string
			clientSecret: string
			stripeLocale: StripeElementLocale
			PublishableKey: string
			showStripeTerms: boolean
		},
		ref,
	) => {
		const {t} = useTranslation()
		const stripe = useSuspenseQuery(stripeQuery(PublishableKey, t))

		return (
			<Elements
				stripe={stripe.data}
				options={{
					locale: stripeLocale,
					clientSecret: clientSecret,
					fonts: [{cssSrc: "https://fonts.googleapis.com/css2?family=Manrope:wght@200..800&display=swap"}],
					appearance: {
						rules: {
							".Input": {
								fontFamily: "Manrope,Roboto,sans-serif",
								padding: "0.75rem",
								borderRadius: "0.5rem",
								border: "1px solid #e5e5e5",
								backgroundColor: "#f6f6f6",
								fontSize: "1rem",
								transition: "none",
							},
							".Input::placeholder": {
								fontFamily: "Manrope,Roboto,sans-serif",
								color: "#737373",
								fontSize: "1rem",
								fontWeight: "400",
								lineHeight: "1.25rem",
								letterSpacing: "0rem",
							},
							".Input:focus": {
								boxShadow: "0px 0px 0px 0px #000",
								border: "1px solid #e5e5e5",
								transition: "none",
							},
							".Input:focus-visible": {
								outline: "-webkit-focus-ring-color auto 1px",
							},
							".Label": {
								fontFamily: "Manrope,Roboto,sans-serif",
								color: "#737373",
								fontSize: "0.875rem",
								fontWeight: "500",
								lineHeight: "1.25rem",
								letterSpacing: "0rem",
								paddingBottom: "0.5rem",
								marginBottom: "0",
							},
							".TermsText": {
								fontFamily: "Manrope,Roboto,sans-serif",
								color: "#737373",
								fontSize: "0.875rem",
								fontWeight: "500",
								lineHeight: "1.25rem",
								letterSpacing: "0rem",
							},
						},
					},
				}}
			>
				<PaymentMethodElement
					ref={ref}
					CustomerCountryISOAlpha={CustomerCountryISOAlpha}
					clientSecret={clientSecret}
					stripe={stripe.data}
					showStripeTerms={showStripeTerms}
				/>
			</Elements>
		)
	},
)

const PaymentMethodElement = forwardRef(
	(
		{
			CustomerCountryISOAlpha,
			clientSecret,
			stripe,
			showStripeTerms,
		}: {
			CustomerCountryISOAlpha: string
			clientSecret: string
			stripe: Stripe
			showStripeTerms: boolean
		},
		ref,
	) => {
		const elements = useElements()

		const {t} = useTranslation()

		useImperativeHandle(ref, () => {
			return {
				createConfirmationToken: async (form: {Name: string; Email: string}) => {
					// See https://stripe.com/docs/stripe-js/react#useelements-hook
					if (!stripe || !elements) {
						throw new Error("Stripe or elements is not initialized")
					}

					const elementsResponse = await elements.submit()
					if (elementsResponse.error) {
						throw new Error(elementsResponse.error.message)
					}

					const confirmationToken = await stripe.createConfirmationToken({
						elements: elements,
						params: {
							payment_method_data: {
								billing_details: {
									name: form.Name,
									email: form.Email,
									address: {
										country: CustomerCountryISOAlpha,
									},
								},
							},
						},
					})

					if (confirmationToken.error) {
						throw new Error(confirmationToken.error.message)
					}

					return confirmationToken.confirmationToken
				},

				confirmSetup: async (confirmationTokenID: string) =>
					confirmSetup(stripe, clientSecret, confirmationTokenID, t),
			}
		})

		return (
			<PaymentElement
				options={{
					layout: "tabs",
					terms: {
						card: showStripeTerms ? "auto" : "never",
					},
					defaultValues: {
						billingDetails: {
							address: {
								country: CustomerCountryISOAlpha,
							},
						},
					},
					fields: {
						billingDetails: {
							name: "never",
							email: "never",
							address: {
								country: "never",
							},
						},
					},
				}}
			/>
		)
	},
)

// Stripe is not exporting the type. So we get manually. We can remove it after they fix it.
export type ConfirmationToken = NonNullable<Awaited<ReturnType<Stripe["createConfirmationToken"]>>["confirmationToken"]>

export type PaymentMethodPreview = NonNullable<
	Awaited<ReturnType<Stripe["createConfirmationToken"]>>["confirmationToken"]
>["payment_method_preview"]
